import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import {
	faAddressBook,
	faBoxArchive,
	faCircleCheck,
	faDatabase,
	faFilter,
	faListCheck,
	faSparkles,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";

import { ListEmptyStateVariant } from "../utils/getListEmptyState";

interface ListEmptyStateProps {
	variant: ListEmptyStateVariant;
	/** Disqualified-lead breakdown shown in the `filtered` variant. */
	counts: { unsure: number; notFound: number; blacklist: number };
	/** Whether the list's source type supports adding a source (hidden for EMPTY / LI_CONNECTIONS). */
	canAddSource: boolean;
	/** Whether the "View ignored leads" action applies (there are ignored leads and we're not on that tab). */
	showViewIgnored: boolean;
	onAddSource: () => void;
	onGoToContacts: () => void;
	onViewIgnored: () => void;
	onReviewAll: () => void;
}

const ART_BY_VARIANT: Record<
	ListEmptyStateVariant,
	{ icon: typeof faSparkles; wrapperClass: string; iconClass: string }
> = {
	never: { icon: faSparkles, wrapperClass: "bg-light-blue", iconClass: "text-brand-light" },
	aged: {
		icon: faCircleCheck,
		wrapperClass: "bg-status-success-light",
		iconClass: "text-status-success",
	},
	filtered: {
		icon: faFilter,
		wrapperClass: "bg-status-warning-light",
		iconClass: "text-status-warning",
	},
};

/**
 * Designed empty states for a lead list table (PREC-3542, mockup
 * "signalist-empty-state-v1"). Rendered inside the table's centered empty slot;
 * the variant is chosen by {@link getListEmptyState}.
 */
export const ListEmptyState: FunctionComponent<ListEmptyStateProps> = ({
	variant,
	counts,
	canAddSource,
	showViewIgnored,
	onAddSource,
	onGoToContacts,
	onViewIgnored,
	onReviewAll,
}) => {
	const { t } = useTranslation();
	const te = (key: string, options?: Record<string, unknown>) =>
		t(`inbox.emptyState.${key}`, options ?? {});

	const art = ART_BY_VARIANT[variant];
	// Counts originate from string API fields — coerce so the total adds instead of concatenating.
	const reviewCount = Number(counts.unsure) + Number(counts.notFound) + Number(counts.blacklist);

	return (
		<div className="mx-auto flex max-w-md flex-col items-center rounded-2xl border border-border-light bg-white px-11 py-10 text-center shadow-sm">
			<div
				className={`mb-5 flex h-[72px] w-[72px] items-center justify-center rounded-[18px] ${art.wrapperClass}`}
			>
				<Icon className={`h-8 w-8 ${art.iconClass}`} icon={art.icon} />
			</div>

			<h2 className="mb-2.5 text-lg font-bold text-text">{te(`${variant}.title`)}</h2>
			<p className="mb-6 text-sm leading-6 text-text-meta">{te(`${variant}.description`)}</p>

			{variant === "filtered" && (
				<div className="mb-6 flex flex-wrap justify-center gap-2">
					<BreakdownChip
						count={counts.unsure}
						dotClass="bg-status-warning"
						label={te("filtered.unsure")}
					/>
					<BreakdownChip
						count={counts.notFound}
						dotClass="bg-text-disabled"
						label={te("filtered.notFound")}
					/>
					<BreakdownChip
						count={counts.blacklist}
						dotClass="bg-status-error"
						label={te("filtered.blacklist")}
					/>
				</div>
			)}

			<div className="flex flex-wrap items-center justify-center gap-2.5">
				{variant === "filtered" ? (
					<Button
						color={ButtonColor.ACTION}
						image={<Icon className="mr-2" icon={faListCheck} />}
						title={te("filtered.reviewAll", { count: reviewCount })}
						onClick={onReviewAll}
					/>
				) : (
					<>
						{canAddSource && (
							<Button
								color={ButtonColor.ACTION}
								image={<Icon className="mr-2" icon={faDatabase} />}
								title={te(`${variant}.addSource`)}
								onClick={onAddSource}
							/>
						)}
						{variant === "never" && (
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								image={<Icon className="mr-2" icon={faAddressBook} />}
								title={te("never.goToContacts")}
								onClick={onGoToContacts}
							/>
						)}
						{variant === "aged" && showViewIgnored && (
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								image={<Icon className="mr-2" icon={faBoxArchive} />}
								title={te("aged.viewIgnored")}
								onClick={onViewIgnored}
							/>
						)}
					</>
				)}
			</div>
		</div>
	);
};

const BreakdownChip: FunctionComponent<{ count: number; dotClass: string; label: string }> = ({
	count,
	dotClass,
	label,
}) => (
	<div className="flex items-center gap-2 rounded-[9px] border border-border-light bg-white px-3 py-2 text-sm">
		<span className={`h-2 w-2 flex-shrink-0 rounded-full ${dotClass}`} />
		<b className="font-bold text-text">{count}</b>
		<span className="text-text-meta">{label}</span>
	</div>
);
